import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
} from '@tanstack/react-router';
import { EmptyState, LoadingScreen, UIProvider } from '@kiipu/ui';
import { ToastProvider } from './context';
import { Library } from './pages/library';
import { AuthPage } from './pages/auth';
import './i18n';
import '@kiipu/ui/styles.css';
import './styles.css';
import './styles/desktop.css';
import { isDesktop } from './lib/client-platform';
import { prepareDesktop } from './lib/desktop-bootstrap';
import { DesktopFrame } from './components/desktop-frame';
import { parseLibrarySearch, type LibrarySearch } from './lib/collection-filter-routing';
export type { LibrarySearch } from './lib/collection-filter-routing';
const DesktopReader = React.lazy(() => import('./components/desktop-reader'));

const SettingsPage = React.lazy(() => import('./pages/settings'));
const AdminPage = React.lazy(() => import('./pages/admin'));
const AdminIndex = React.lazy(() =>
  import('./pages/admin').then((module) => ({ default: module.AdminIndex })),
);
const AdminSection = React.lazy(() =>
  import('./pages/admin').then((module) => ({ default: module.AdminSection })),
);
const AuthorizePage = React.lazy(() => import('./pages/authorize'));
const HelpPage = React.lazy(() =>
  import('./pages/information').then((module) => ({ default: module.HelpPage })),
);
const PrivacyPage = React.lazy(() =>
  import('./pages/information').then((module) => ({ default: module.PrivacyPage })),
);
const AboutPage = React.lazy(() =>
  import('./pages/information').then((module) => ({ default: module.AboutPage })),
);
const TermsPage = React.lazy(() =>
  import('./pages/information').then((module) => ({ default: module.TermsPage })),
);
const rootRoute = createRootRoute({
  component: () => (
    <Suspense fallback={<LoadingScreen />}>
      <DesktopFrame>
        <Outlet />
      </DesktopFrame>
    </Suspense>
  ),
  notFoundComponent: () => (
    <main className="standalone">
      <EmptyState
        title="页面不存在"
        description="这个页面可能已经移除，或链接地址有误。"
        action={
          <a href="/" className="button button-secondary">
            返回内容库
          </a>
        }
      />
    </main>
  ),
});
const validateLibrarySearch = parseLibrarySearch;
const libraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: validateLibrarySearch,
  component: Library,
});
const globalSearchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search',
  validateSearch: validateLibrarySearch,
  component: Library,
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: () => <AuthPage />,
});
const desktopReaderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/desktop/reader',
  validateSearch: (search: Record<string, unknown>) => ({
    item: typeof search.item === 'string' ? search.item : '',
  }),
  component: DesktopReader,
});
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: () => <AuthPage register />,
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
});
const settingsSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/$section',
  component: SettingsPage,
});
const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/help',
  component: HelpPage,
});
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPage,
});
const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/about',
  component: AboutPage,
});
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsPage,
});
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminPage,
  validateSearch: (search: Record<string, unknown>) =>
    Object.fromEntries(
      Object.entries(search).filter(
        ([key, value]) =>
          [
            'q',
            'status',
            'verified',
            'userId',
            'itemId',
            'platform',
            'quality',
            'deleted',
            'targetType',
            'targetUserId',
            'tab',
            'cursor',
            'days',
            'back',
          ].includes(key) && typeof value === 'string',
      ),
    ) as import('./pages/admin/shared').AdminSearch,
});
const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: AdminIndex,
});
const adminSectionRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '$section',
  component: AdminSection,
});
const adminDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '$section/$detail',
  component: AdminSection,
});
const authorizeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/extension/authorize',
  validateSearch: (search: Record<string, unknown>) => ({
    flowId: typeof search.flowId === 'string' ? search.flowId : '',
  }),
  component: AuthorizePage,
});
const authorizeAlias = createRoute({
  getParentRoute: () => rootRoute,
  path: '/authorize',
  validateSearch: (search: Record<string, unknown>) => ({
    flowId: typeof search.flowId === 'string' ? search.flowId : '',
  }),
  component: AuthorizePage,
});
const router = createRouter({
  routeTree: rootRoute.addChildren([
    libraryRoute,
    globalSearchRoute,
    desktopReaderRoute,
    loginRoute,
    registerRoute,
    settingsRoute,
    settingsSectionRoute,
    helpRoute,
    privacyRoute,
    aboutRoute,
    termsRoute,
    adminRoute.addChildren([adminIndexRoute, adminSectionRoute, adminDetailRoute]),
    authorizeRoute,
    authorizeAlias,
  ]),
  defaultPreload: 'intent',
  scrollRestoration: ({ location }) => location.pathname.startsWith('/admin'),
  getScrollRestorationKey: (location) => {
    if (!location.pathname.startsWith('/admin')) return location.state.__TSR_key || location.href;
    const search = new URLSearchParams(location.searchStr);
    search.sort();
    return `${location.pathname}?${search}`;
  },
  scrollToTopSelectors: ['.admin-workspace'],
});
declare module '@tanstack/react-router' {
  interface HistoryState {
    kiipuFilterLive?: boolean;
  }
  interface Register {
    router: typeof router;
  }
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: true,
      ...(isDesktop() ? { networkMode: 'always' } : {}),
    },
    mutations: { retry: false, ...(isDesktop() ? { networkMode: 'always' } : {}) },
  },
});
async function start() {
  const observe = await prepareDesktop(queryClient);
  observe?.((href) => {
    void router.navigate({ href });
  });
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <UIProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </UIProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
void start().catch(() => {
  const root = document.getElementById('root');
  if (root) root.textContent = 'Kiipu 未能恢复桌面会话，请重新启动应用。';
});
